import moment from 'moment-timezone';

export const UK_TIMEZONE = 'Europe/London';

const UK_LABEL = 'United Kingdom';
const UK_KEYWORDS = 'uk u.k. united kingdom britain great britain england scotland wales northern ireland london bst gmt';
const RECOMMENDED_GROUP = 'Recommended';

const HIDDEN_PATTERN = /^Etc\/GMT[+-]\d+$/;
const HIDDEN_ZONES = new Set([
  'Etc/GMT', 'Etc/GMT0', 'GMT', 'GMT0', 'GMT+0', 'GMT-0', 'Greenwich', 'Etc/Greenwich',
  'UCT', 'Etc/UCT', 'Zulu', 'Etc/Zulu',
]);

export interface TimezoneOption {
  name: string;
  nameValue: string;
  timeValue: string;
  group: string;
  abbr: string;
  keywords: string;
}

export const isHiddenTimezone = (zone: string): boolean =>
  HIDDEN_PATTERN.test(zone) || HIDDEN_ZONES.has(zone);

const offsetOf = (zone: string): string => moment.tz(zone).format('Z');

const ukOption = (): TimezoneOption => {
  const utc = offsetOf(UK_TIMEZONE);
  return {
    name: `${UK_LABEL} (${UK_TIMEZONE}) (${utc})`,
    nameValue: UK_TIMEZONE,
    timeValue: utc,
    group: RECOMMENDED_GROUP,
    abbr: moment.tz(UK_TIMEZONE).zoneAbbr(),
    keywords: UK_KEYWORDS,
  };
};

const zoneOption = (zone: string, label?: string): TimezoneOption => {
  const utc = offsetOf(zone);
  return {
    name: label || `${zone} (${utc})`,
    nameValue: zone,
    timeValue: utc,
    group: zone.split('/', 1)[0],
    abbr: moment.tz(zone).zoneAbbr(),
    keywords: '',
  };
};

export const buildTimezoneOptions = (selected?: string | null): TimezoneOption[] => {
  const options: TimezoneOption[] = [ukOption()];
  for (const zone of moment.tz.names()) {
    if (zone === UK_TIMEZONE || isHiddenTimezone(zone)) {
      continue;
    }
    options.push(zoneOption(zone));
  }
  if (selected && selected !== UK_TIMEZONE && isHiddenTimezone(selected) && moment.tz.zone(selected)) {
    options.push(zoneOption(selected, `${selected} (UTC${offsetOf(selected)}, not UK time)`));
  }
  return options;
};

export const timezoneSearch = (term: string, item: { name?: string; keywords?: string }): boolean => {
  const needle = (term || '').trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const haystack = `${item.name || ''} ${item.keywords || ''}`.toLowerCase();
  if (needle.length > 2) {
    return haystack.includes(needle);
  }
  return haystack.split(/[^a-z0-9.]+/).some(word => word.startsWith(needle));
};
